import { Link } from 'react-router-dom';
import type { ListingCard as ListingCardData } from '@biyolink/shared';
import { formatNumber, formatQuantity } from '@/lib/format';
import { t } from '@/i18n';

/**
 * İlan kartı — vitrin, ana sayfa, firma profili ve favorilerde AYNI bileşen kullanılır
 * (docs/05 §2 "her listede aynı bileşen").
 */
export function ListingCard({ listing }: { listing: ListingCardData }) {
  return (
    <article className="group overflow-hidden rounded-card border border-slate-200 bg-surface shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <Link to={`/ilan/${listing.slug}`} className="block">
        {/* 3:2 çerçeve (müşteri, 21.09.2026): kare kart ilk ekranda tek satır ürün
            bırakıyordu; dört sütunla birlikte bu oran iki satırı ekrana sığdırıyor.
            `object-cover` kalıyor ki gerçek ilan fotoğrafları çerçeveyi doldursun. */}
        <div className="relative aspect-[3/2] overflow-hidden bg-slate-100">
          {listing.coverImage ? (
            <img
              src={listing.coverImage.url}
              alt={t('listing.coverAlt', { title: listing.title })}
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="grid h-full place-items-center text-sm text-slate-400">
              {t('common.none')}
            </div>
          )}

          {/*
            Uzaklık rozeti görselin ÜSTÜNDE (21.09.2026): alt bilgi satırına sığmıyor,
            dar kartta kırpılıyordu. Yalnız aktif tesisi olan alıcıda gelir ve kesin km
            değil 100 km'ye yuvarlanmış üst sınırdır (docs/09 §1.3).
          */}
          {listing.distanceBucketKm !== null && (
            <p className="absolute bottom-2 left-2 rounded-full bg-surface/90 px-2 py-0.5 text-[11px] font-medium text-slate-700 shadow-sm">
              <span className="sr-only">{t('listings.sortDistance')}: </span>
              {t('listings.distanceUpTo', { km: listing.distanceBucketKm })}
            </p>
          )}
        </div>

        {/* Kart kısaltıldı (müşteri, 21.09.2026): "Biyolink'ten · görüntülenme"
            şeridi kalktı, iç boşluklar daraldı. Satıcı adı zaten hiç yazmıyordu. */}
        <div className="px-3 py-2.5">
          <p className="text-[10.5px] font-medium uppercase leading-4 tracking-wide text-primary-900">
            {listing.category.name}
          </p>

          <h3 className="mt-0.5 line-clamp-2 min-h-[2.1rem] text-[13px] font-semibold leading-[1.3] text-slate-900">
            {listing.title}
          </h3>

          {/*
            Alt satır tek satırda kalır: solda miktar, sağda görüntülenme (müşteri,
            21.09.2026). Fiyat ibaresi karttan KALKTI — vitrinde zaten hiçbir ilanın
            fiyatı yok, her kartta aynı cümleyi tekrarlamak üç satır yer kaplıyordu.
            Satıcının ili/ilçesi de görünmez (docs/12).
          */}
          <dl className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-600">
            <div className="min-w-0 truncate">
              <dt className="sr-only">{t('listingForm.fieldQuantity')}</dt>
              <dd>{listing.quantity && formatQuantity(listing.quantity, listing.unit)}</dd>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <dt className="sr-only">
                {t('listingDetail.viewCount', { count: listing.viewCount })}
              </dt>
              <dd aria-hidden="true" className="flex items-center gap-1">
                <span>👁</span>
                {formatNumber(listing.viewCount)}
              </dd>
            </div>
          </dl>
        </div>
      </Link>
    </article>
  );
}

/**
 * İlan kartlarını sabit bir grid içinde gösterir.
 *
 * Geniş ekranda DÖRT sütun (müşteri, 21.09.2026): kart kısaldığı için ilk ekranda
 * iki satır ürün görünüyor.
 */
export function ListingGrid({ listings }: { listings: ListingCardData[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {listings.map((listing) => (
        <ListingCard key={listing.id} listing={listing} />
      ))}
    </div>
  );
}
